// ── useScopeSelection — Namespace & scope key selection state + helpers ─────
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchWorkflowScopesApi,
  fetchScopeNamespacesApi,
} from '../../../api/scopesApi';
import type { WorkflowScope, ScopeNamespaceCount } from '../types';
import { SCOPE_KEY_LIMIT } from '../types';
import { _sk, decodeScopeKey, scopeKeyDisplay } from '../utils';

export function useScopeSelection(activeTenantId: number | '' | undefined) {
  const activeTid =
    activeTenantId !== '' && activeTenantId !== undefined
      ? activeTenantId
      : undefined;

  /* ── State ─────────────────────────────────────────────────── */
  const [selectedNs, setSelectedNs] = useState<string | null>(null);
  const [nsSearch, setNsSearch] = useState('');
  const [keySearch, setKeySearch] = useState('');
  const [selectedScopeIds, setSelectedScopeIds] = useState<Set<number>>(
    new Set(),
  );
  const [loadedNsScopes, setLoadedNsScopes] = useState<
    Map<string, number[]>
  >(new Map());
  const [loadedNsScopeData, setLoadedNsScopeData] = useState<
    Map<string, WorkflowScope[]>
  >(new Map());
  const [loadingNs, setLoadingNs] = useState<Set<string>>(new Set());

  /* ── Namespace query ───────────────────────────────────────── */
  const { data: namespacesRaw = [], isLoading: nsLoading } = useQuery<
    ScopeNamespaceCount[]
  >({
    queryKey: ['migrate', 'scopeNamespaces', activeTid],
    queryFn: async () => {
      const res = await fetchScopeNamespacesApi(activeTid);
      return (res?.items ?? []) as ScopeNamespaceCount[];
    },
    staleTime: 30_000,
  });

  const namespaces = useMemo(() => {
    const term = nsSearch.trim().toLowerCase();
    if (!term) return namespacesRaw;
    return namespacesRaw.filter((n) =>
      n.scope_namespace.toLowerCase().includes(term),
    );
  }, [namespacesRaw, nsSearch]);

  const totalScopeCount = useMemo(
    () =>
      namespacesRaw.reduce(
        (sum: number, n: ScopeNamespaceCount) => sum + n.count,
        0,
      ),
    [namespacesRaw],
  );

  /* ── Scope keys for selected namespace ─────────────────────── */
  const { data: scopeKeysRaw = [], isLoading: keysLoading } = useQuery<
    WorkflowScope[]
  >({
    queryKey: ['migrate', 'scopeKeys', selectedNs, activeTid],
    queryFn: async () => {
      if (!selectedNs) return [];
      const res = await fetchWorkflowScopesApi({
        scope_namespace: selectedNs,
        limit: SCOPE_KEY_LIMIT,
        tenant_id: activeTid,
      });
      return (res?.items ?? res ?? []) as WorkflowScope[];
    },
    enabled: !!selectedNs,
    staleTime: 30_000,
  });

  /* ── Cache scope IDs + data per namespace ──────────────────── */
  useEffect(() => {
    if (!selectedNs || scopeKeysRaw.length === 0) return;
    const ids = scopeKeysRaw
      .map((s) => s.id_scope!)
      .filter((id) => id != null);
    setLoadedNsScopes((prev) => {
      const existing = prev.get(selectedNs);
      if (
        existing &&
        existing.length === ids.length &&
        existing.every((v, i) => v === ids[i])
      )
        return prev;
      const next = new Map(prev);
      next.set(selectedNs, ids);
      return next;
    });
    setLoadedNsScopeData((prev) => {
      const existing = prev.get(selectedNs);
      if (existing && existing.length === scopeKeysRaw.length) return prev;
      const next = new Map(prev);
      next.set(selectedNs, scopeKeysRaw);
      return next;
    });
  }, [selectedNs, scopeKeysRaw]);

  const filteredScopes = useMemo(() => {
    const term = keySearch.trim().toLowerCase();
    if (!term) return scopeKeysRaw;
    return scopeKeysRaw.filter((s) => {
      const display = scopeKeyDisplay(s).toLowerCase();
      const raw = _sk(s).toLowerCase();
      const decoded = decodeScopeKey(_sk(s));
      const label = decoded?.label?.toLowerCase() ?? '';
      return (
        display.includes(term) || raw.includes(term) || label.includes(term)
      );
    });
  }, [scopeKeysRaw, keySearch]);

  /* ── Toggle helpers ────────────────────────────────────────── */
  function toggleScopeId(id: number) {
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllScopes() {
    const allIds = filteredScopes
      .map((s) => s.id_scope!)
      .filter((id) => id != null);
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        allIds.length > 0 && allIds.every((id) => next.has(id));
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  async function toggleNamespaceScopes(ns: string) {
    let ids = loadedNsScopes.get(ns);
    if (!ids) {
      setLoadingNs((prev) => {
        const n = new Set(prev);
        n.add(ns);
        return n;
      });
      try {
        const res = await fetchWorkflowScopesApi({
          scope_namespace: ns,
          limit: SCOPE_KEY_LIMIT,
          tenant_id: activeTid,
        });
        const scopes = (res?.items ?? res ?? []) as WorkflowScope[];
        ids = scopes
          .map((s: WorkflowScope) => s.id_scope!)
          .filter((id: number) => id != null);
        setLoadedNsScopes((prev) => {
          const next = new Map(prev);
          next.set(ns, ids!);
          return next;
        });
        setLoadedNsScopeData((prev) => {
          const next = new Map(prev);
          next.set(ns, scopes);
          return next;
        });
      } catch {
        return;
      } finally {
        setLoadingNs((prev) => {
          const n = new Set(prev);
          n.delete(ns);
          return n;
        });
      }
    }
    if (ids.length === 0) return;
    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids!.every((id: number) => next.has(id));
      if (allSelected) {
        ids!.forEach((id: number) => next.delete(id));
      } else {
        ids!.forEach((id: number) => next.add(id));
      }
      return next;
    });
  }

  async function toggleAllNamespaces() {
    const visibleNs = namespaces.map(
      (n: ScopeNamespaceCount) => n.scope_namespace,
    );
    const cached = new Map(loadedNsScopes);
    const toFetch = visibleNs.filter((ns: string) => !cached.has(ns));
    if (toFetch.length > 0) {
      setLoadingNs(new Set(toFetch));
      try {
        const dataCached = new Map(loadedNsScopeData);
        const results = await Promise.allSettled(
          toFetch.map(async (ns: string) => {
            const res = await fetchWorkflowScopesApi({
              scope_namespace: ns,
              limit: SCOPE_KEY_LIMIT,
              tenant_id: activeTid,
            });
            const scopes = (res?.items ?? res ?? []) as WorkflowScope[];
            return {
              ns,
              scopes,
              ids: scopes
                .map((s: WorkflowScope) => s.id_scope!)
                .filter((id: number) => id != null),
            };
          }),
        );
        for (const r of results) {
          if (r.status === 'fulfilled') {
            cached.set(r.value.ns, r.value.ids);
            dataCached.set(r.value.ns, r.value.scopes);
          }
        }
        setLoadedNsScopes(new Map(cached));
        setLoadedNsScopeData(new Map(dataCached));
      } finally {
        setLoadingNs(new Set());
      }
    }

    const visibleIds: number[] = [];
    for (const ns of visibleNs) {
      const ids = cached.get(ns);
      if (ids) visibleIds.push(...ids);
    }

    const allSelected =
      visibleIds.length > 0 &&
      visibleIds.every((id) => selectedScopeIds.has(id));

    setSelectedScopeIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  /* ── Derived selection state ───────────────────────────────── */
  const allFilteredSelected =
    filteredScopes.length > 0 &&
    filteredScopes.every(
      (s) => s.id_scope != null && selectedScopeIds.has(s.id_scope),
    );

  const someFilteredSelected =
    !allFilteredSelected &&
    filteredScopes.length > 0 &&
    filteredScopes.some(
      (s) => s.id_scope != null && selectedScopeIds.has(s.id_scope),
    );

  const nsSelectionState = useMemo(() => {
    const map = new Map<string, 'all' | 'partial' | 'none'>();
    const entries = Array.from(loadedNsScopes.entries());
    entries.forEach(([ns, ids]) => {
      const scopeIds: number[] = ids ?? [];
      if (scopeIds.length === 0) {
        map.set(ns, 'none');
        return;
      }
      const sel = scopeIds.filter((id: number) =>
        selectedScopeIds.has(id),
      ).length;
      map.set(
        ns,
        sel === 0 ? 'none' : sel === scopeIds.length ? 'all' : 'partial',
      );
    });
    return map;
  }, [loadedNsScopes, selectedScopeIds]);

  const allNsSelectState = useMemo((): 'all' | 'partial' | 'none' => {
    if (namespaces.length === 0) return 'none';
    const loaded = namespaces.filter((n: ScopeNamespaceCount) =>
      loadedNsScopes.has(n.scope_namespace),
    );
    if (loaded.length === 0 && selectedScopeIds.size === 0) return 'none';
    const allAll =
      loaded.length === namespaces.length &&
      loaded.every(
        (n: ScopeNamespaceCount) =>
          nsSelectionState.get(n.scope_namespace) === 'all',
      );
    if (allAll) return 'all';
    const anyVisible = loaded.some(
      (n: ScopeNamespaceCount) =>
        nsSelectionState.get(n.scope_namespace) !== 'none',
    );
    return anyVisible
      ? 'partial'
      : selectedScopeIds.size > 0
        ? 'partial'
        : 'none';
  }, [namespaces, loadedNsScopes, nsSelectionState, selectedScopeIds.size]);

  /* ── Cross-namespace aggregates ────────────────────────────── */
  const allSelectedScopes = useMemo(() => {
    const result: WorkflowScope[] = [];
    const entries = Array.from(loadedNsScopeData.entries());
    entries.forEach(([, scopes]) => {
      for (const s of scopes) {
        if (s.id_scope != null && selectedScopeIds.has(s.id_scope))
          result.push(s);
      }
    });
    return result;
  }, [loadedNsScopeData, selectedScopeIds]);

  const allCachedScopes = useMemo(() => {
    const result: WorkflowScope[] = [];
    const entries = Array.from(loadedNsScopeData.entries());
    entries.forEach(([, scopes]) => result.push(...scopes));
    return result;
  }, [loadedNsScopeData]);

  /* ── Reset ─────────────────────────────────────────────────── */
  function resetSelection() {
    setSelectedNs(null);
    setSelectedScopeIds(new Set());
    setLoadedNsScopes(new Map());
    setLoadedNsScopeData(new Map());
  }

  return {
    selectedNs,
    setSelectedNs,
    nsSearch,
    setNsSearch,
    keySearch,
    setKeySearch,
    selectedScopeIds,
    setSelectedScopeIds,
    loadedNsScopes,
    loadedNsScopeData,
    loadingNs,
    namespacesRaw,
    namespaces,
    nsLoading,
    keysLoading,
    totalScopeCount,
    filteredScopes,
    toggleScopeId,
    toggleAllScopes,
    toggleNamespaceScopes,
    toggleAllNamespaces,
    allFilteredSelected,
    someFilteredSelected,
    nsSelectionState,
    allNsSelectState,
    allSelectedScopes,
    allCachedScopes,
    resetSelection,
  };
}
